// 契約によるログイン制御（管理画面化・SPEC §7.4 / §7.4.6）。BE の 401 に付く code と表示文言
export type ClientAccessCode = "CONTRACT_NOT_STARTED" | "CONTRACT_EXPIRED";

export const CLIENT_ACCESS_MESSAGES: Record<ClientAccessCode, string> = {
  CONTRACT_NOT_STARTED: "ご契約の開始日前のためログインできません。",
  CONTRACT_EXPIRED: "契約期間が終了しています。継続をご希望の場合はお問い合わせください。",
};

/** 解約後の猶予期間中に AI の API を呼んだときの 403 の code（§7.4.6） */
export const AI_UNAVAILABLE_CODE = "AI_UNAVAILABLE_AFTER_CONTRACT_END";

/** トライアル中に AI の無料枠（5 万円）を使い切ったあと AI の API を呼んだときの 403 の code */
export const TRIAL_AI_LIMIT_CODE = "TRIAL_AI_LIMIT_EXCEEDED";

const AI_BLOCKED_CODES: unknown[] = [AI_UNAVAILABLE_CODE, TRIAL_AI_LIMIT_CODE];

/**
 * AI 機能を止めた 403 なら、BE の文言（利用者に理由を伝える）を返す。そうでなければ null。
 * code は物件管理 BE のエラーハンドラーでは code / error、ippon-ai-common では error.code に入る
 */
export const resolveAiBlockedMessage = (payload: unknown): string | null => {
  if (!payload || typeof payload !== "object") return null;
  const { code, error, message } = payload as { code?: unknown; error?: unknown; message?: unknown };
  const nestedCode = error && typeof error === "object" ? (error as { code?: unknown }).code : undefined;
  if (![code, error, nestedCode].some((value) => AI_BLOCKED_CODES.includes(value))) return null;
  return typeof message === "string" && message ? message : null;
};

export const isClientAccessCode = (value: unknown): value is ClientAccessCode =>
  value === "CONTRACT_NOT_STARTED" || value === "CONTRACT_EXPIRED";

/** 強制ログアウトの理由を渡すキー。ログイン画面で 1 回だけ読んで消す */
export const LOGOUT_REASON_KEY = "logoutReason";

type ReasonStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const sessionStore = (): ReasonStorage | null =>
  typeof window !== "undefined" && window.sessionStorage ? window.sessionStorage : null;

/** 強制ログアウトの理由を保存する。契約以外の 401（トークン切れなど）は保存しない */
export const saveLogoutReason = (code: unknown, storage: ReasonStorage | null = sessionStore()): void => {
  if (storage && isClientAccessCode(code)) storage.setItem(LOGOUT_REASON_KEY, code);
};

/** 保存した理由の文言を 1 回だけ返す（読んだら消す）。無ければ null */
export const consumeLogoutReason = (storage: ReasonStorage | null = sessionStore()): string | null => {
  if (!storage) return null;
  const code = storage.getItem(LOGOUT_REASON_KEY);
  storage.removeItem(LOGOUT_REASON_KEY);
  return isClientAccessCode(code) ? CLIENT_ACCESS_MESSAGES[code] : null;
};

/** ログインに失敗したときの文言（契約終了・契約前・パスワード違いなどを区別しない） */
export const SIGN_IN_FAILED_MESSAGE = "ログインに失敗しました";

/**
 * ログインの失敗文言。契約終了・契約前で止められた 401 も含め「ログインに失敗しました」にする。
 * reCAPTCHA 不合格（403）・ロックアウト（429）だけは、利用者が次に何をすればよいかが分かるよう BE の文言を出す
 */
export const resolveSignInErrorMessage = (payload: { status?: number; code?: unknown; message?: unknown } | undefined): string =>
  (payload?.status === 403 || payload?.status === 429) && typeof payload.message === "string" && payload.message
    ? payload.message
    : SIGN_IN_FAILED_MESSAGE;

/**
 * 401 のときに強制ログアウトしてログイン画面へ移すか。
 * ログイン API の 401（パスワード違い・契約終了など）はログイン画面にとどまって失敗の文言を出すため、移さない
 */
export const shouldForceLogoutOn401 = (url: string | undefined): boolean =>
  !(url ?? "").split("?")[0].endsWith("/auth/signin");
